import React, { createContext, useContext, useState } from 'react';
import styled from 'styled-components';

// Types
interface TabsContextType {
  value: string;
  onValueChange: (value: string) => void;
}

interface TabsProps {
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
  className?: string;
}

interface TabsListProps {
  children: React.ReactNode;
  className?: string;
}

interface TabsTriggerProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

interface TabsContentProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

// Context
const TabsContext = createContext<TabsContextType | undefined>(undefined);

const useTabsContext = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tabs components must be used within a Tabs component');
  }
  return context;
};

// Styled Components
const StyledTabsContainer = styled.div`
  width: 100%;
`;

const StyledTabsList = styled.div`
  display: flex;
  background: transparent;
  border: none;
  border-bottom: 1px solid ${props => props.theme.border};
  border-radius: 0;
  padding: 0;
  gap: 0;
`;

const StyledTabsTrigger = styled.button<{ $isActive: boolean }>`
  flex: 1;
  padding: 12px 16px;
  border: none;
  outline: none;
  box-shadow: none;
  background: transparent;
  color: ${props => (props.$isActive ? props.theme.foreground : props.theme.mutedForeground)};
  border-radius: 0;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease;
  font-size: 14px;
  font-weight: ${props => (props.$isActive ? '600' : '500')};
  position: relative;

  &:hover {
    background: ${props => props.theme.muted};
    color: ${props => props.theme.foreground};
  }

  &:focus,
  &:focus-visible,
  &:active {
    outline: none;
    box-shadow: none;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 2px;
    background: ${props => (props.$isActive ? props.theme.foreground : 'transparent')};
    border-radius: 0;
  }
`;

const StyledTabsContent = styled.div<{ $isActive: boolean }>`       
  display: ${props => props.$isActive ? 'block' : 'none'};    
  padding: 16px 0;
`;

// Tabs Component
export const Tabs: React.FC<TabsProps> = ({ value, onValueChange, children, className }) => {
  return (
    <TabsContext.Provider value={{ value, onValueChange }}>
      <StyledTabsContainer className={className}>
        {children}
      </StyledTabsContainer>
    </TabsContext.Provider>
  );
};

// TabsList Component
export const TabsList: React.FC<TabsListProps> = ({ children, className }) => {
  return (
    <StyledTabsList className={className}>
      {children}
    </StyledTabsList>
  );
};

// TabsTrigger Component
export const TabsTrigger: React.FC<TabsTriggerProps> = ({ value, children, className }) => {
  const { value: currentValue, onValueChange } = useTabsContext();
  const isActive = currentValue === value;

  return (
    <StyledTabsTrigger 
      $isActive={isActive}
      onClick={() => onValueChange(value)}
      className={className}
    >
      {children}
    </StyledTabsTrigger>
  );
};

// TabsContent Component
export const TabsContent: React.FC<TabsContentProps> = ({ value, children, className }) => {
  const { value: currentValue } = useTabsContext();
  const isActive = currentValue === value;

  return (
    <StyledTabsContent $isActive={isActive} className={className}>
      {children}
    </StyledTabsContent>
  );
};
